<template>
  <v-container :class="containerClass" fluid>
    <v-card flat>
      <v-card-title>
        <div class="d-flex align-center">
          <v-btn icon color="primary" left :href="$route('classrooms.index')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>{{ classroom.data.name }}</h2>
        </div>
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="selectedTab">
          <v-tab>Summary</v-tab>
          <v-tab>Schedules</v-tab>
          <v-tab>Holidays</v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedTab">
          <v-tab-item>
            <v-container fluid>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card class="mb-4">
                    <v-card-title> Class Info </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text>
                      <v-container fluid>
                        <v-row>
                          <v-col cols="12" md="4">
                            <h3>Class Date</h3>
                            <div>
                              {{
                                formatDate(
                                  classroom.data.start_date,
                                  'D MMM YYYY'
                                )
                              }}
                              -
                              {{
                                formatDate(
                                  classroom.data.end_date,
                                  'D MMM YYYY'
                                )
                              }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="4">
                            <h3>Class Type</h3>
                            <div>
                              {{
                                classroomTypeProps(
                                  classroom.data.class_type,
                                  'text'
                                )
                              }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="4">
                            <h3>Status</h3>
                            <div>
                              <v-chip
                                :color="
                                  classroomStatusProps(
                                    classroom.data.status,
                                    'color'
                                  )
                                "
                                small
                              >
                                {{
                                  classroomStatusProps(
                                    classroom.data.status,
                                    'text'
                                  )
                                }}
                              </v-chip>
                            </div>
                          </v-col>
                          <v-col cols="12" md="4">
                            <h3>Teacher</h3>
                            <div>{{ classroom.data.teacher.name }}</div>
                          </v-col>
                          <v-col cols="12" md="4">
                            <h3>Lesson Plan</h3>
                            <div>
                              {{
                                classroom.data.subscription_package.lesson_plan
                                  .name
                              }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="4">
                            <h3>Online Class URL</h3>
                            <div>
                              <a
                                v-if="classroom.data.online_class_url"
                                :href="classroom.data.online_class_url"
                              >
                                Open online class here
                              </a>
                              <span v-else>-</span>
                            </div>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                  </v-card>
                  <v-card class="mb-4">
                    <v-card-title>
                      Weekly Schedules
                      <v-spacer></v-spacer>
                      <v-switch v-model="isEdit" label="Edit" inset></v-switch>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text>
                      <v-container fluid>
                        <v-row
                          v-for="(weeklySchedule, j) in weeklySchedules"
                          :key="j"
                          align="center"
                        >
                          <v-col cols="12" md="4"> Session {{ j + 1 }} </v-col>
                          <v-col cols="12" md="4">
                            <v-select
                              v-model="weeklySchedule.day_of_week"
                              :items="dayOfWeekOptions"
                              :disabled="!isEdit"
                              hide-details
                              solo
                              flat
                              outlined
                              dense
                              placeholder="Choose weekday"
                            />
                          </v-col>
                          <v-col cols="12" md="4" class="d-flex align-center">
                            <time-picker
                              v-model="weeklySchedule.start_time"
                              :disabled="!isEdit"
                              input-width="72px"
                              close-on-complete
                              manual-input
                              class="vue__time-picker--dense"
                            />
                            <div class="mx-2">-</div>
                            <time-picker
                              v-model="weeklySchedule.end_time"
                              :disabled="!isEdit"
                              input-width="72px"
                              close-on-complete
                              manual-input
                              class="vue__time-picker--dense"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-card-actions v-show="isEdit">
                      <v-container fluid>
                        <v-row justify="end">
                          <v-col md="4" cols="12">
                            <v-btn
                              color="success"
                              depressed
                              block
                              @click="saveChanges"
                            >
                              Save Changes
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-actions>
                  </v-card>
                  <v-card>
                    <v-card-title> Enrolled Students </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text>
                      <v-list>
                        <template
                          v-for="(student, index) in classroom.data.students"
                        >
                          <v-list-item :key="student.id">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ student.name }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider
                            v-if="index < classroom.data.students.length - 1"
                            :key="`div-${index}`"
                          ></v-divider>
                        </template>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Recent Class Schedules</v-card-title>
                    <v-divider></v-divider>
                    <v-card-text>
                      <template
                        v-for="(classSchedule, i) in recentClassSchedules"
                      >
                        <v-list :key="i" subheader>
                          <v-subheader>
                            <h3>{{ classSchedule.title }}</h3>
                          </v-subheader>
                          <v-list-item
                            v-for="item in classSchedule.items"
                            :key="item.class_date"
                          >
                            <v-list-item-content>
                              <v-container fluid class="py-0">
                                <v-row dense>
                                  <v-col cols="12" md="10">
                                    <v-row align="center" dense>
                                      <v-col cols="6" class="font-weight-bold">
                                        {{
                                          formatDate(
                                            item.class_date,
                                            'D MMM YYYY'
                                          )
                                        }}
                                        <v-chip
                                          v-if="!$vuetify.breakpoint.mobile"
                                          small
                                          class="mx-1"
                                        >
                                          {{ timeFromNow(item.class_date) }}
                                        </v-chip>
                                      </v-col>
                                      <v-col
                                        cols="6"
                                        :class="{
                                          'text-right':
                                            $vuetify.breakpoint.mobile,
                                        }"
                                      >
                                        <v-icon
                                          v-if="!$vuetify.breakpoint.mobile"
                                          left
                                          small
                                        >
                                          mdi-clock-outline
                                        </v-icon>
                                        {{ formatTime(item.start_time) }} -
                                        {{ formatTime(item.end_time) }}
                                      </v-col>
                                      <v-col cols="12">
                                        <div>Lessons:</div>
                                        <ol>
                                          <li
                                            v-for="(lesson, j) in item.lessons"
                                            :key="j"
                                          >
                                            {{
                                              bilingualText(
                                                lesson,
                                                'name',
                                                'name_cn'
                                              )
                                            }}
                                          </li>
                                        </ol>
                                      </v-col>
                                    </v-row>
                                  </v-col>
                                  <v-col cols="12" md="2" class="text-right">
                                    <v-btn
                                      text
                                      small
                                      color="info"
                                      :href="
                                        $route(
                                          'classroom-schedules.update',
                                          item.id
                                        )
                                      "
                                    >
                                      See Details
                                    </v-btn>
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                        <v-divider
                          v-if="i < recentClassSchedules.length - 1"
                          :key="`div-${i}`"
                        ></v-divider>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
          <v-tab-item>
            <schedule-list
              ref="scheduleList"
              :classroom="classroom.data"
              show-add
              show-generate
            ></schedule-list>
          </v-tab-item>
          <v-tab-item>
            <holiday-list :classroom-id="classroomId"></holiday-list>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
    <schedule-form-dialog
      v-if="editDialog.show"
      v-model="editDialog.show"
      :item.sync="editDialog.item"
      submit-api
      @submit="onSubmitEditSchedule"
    ></schedule-form-dialog>
  </v-container>
</template>
<script>
import constant from '@/constant'
import LayoutDashboard from '@layouts/Dashboard'
import HolidayList from '@components/Classroom/HolidayList'
import ScheduleList from '@components/Classroom/ScheduleList'
import { mapActions, mapGetters } from 'vuex'

const thisWeekStart = moment().startOf('week')
const thisWeekEnd = moment().endOf('week')
export default {
  name: 'TeacherClassroomDetail',
  components: {
    ScheduleList,
    HolidayList,
  },
  layout: LayoutDashboard,
  props: {
    classroom: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      panels: [0, 1, 2],
      editDialog: {
        show: false,
        item: null,
        index: -1,
      },
      selectedTab: 0,
      isEdit: false,
      weeklySchedules: [],
    }
  },
  computed: {
    ...mapGetters({
      scheduleList: 'classroomSchedule/list',
      isScheduleListLoading: 'classroomSchedule/isListLoading',
      studyProgressList: 'studyProgress/list',
      isStudyProgressListLoading: 'studyProgress/isListLoading',
    }),
    classroomId() {
      return this.classroom.data.id
    },
    schedules() {
      return this.scheduleList.data.map((e) => {
        const item = _.cloneDeep(e)
        if (item.lessons.length === 0) {
          item.lessons = this.groupedStudyProgressLessons[item.id] ?? []
        }
        return item
      })
    },
    dayOfWeekOptions() {
      return constant.dayOfWeekOptions
    },
    recentClassSchedules() {
      return [
        {
          title: 'This Week',
          items: this.thisWeekSchedules,
        },
        {
          title: 'Last Week',
          items: this.lastWeekSchedules,
        },
        {
          title: 'Next Week',
          items: this.nextWeekSchedules,
        },
      ]
    },
    thisWeekSchedules() {
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(thisWeekStart) && dt.isSameOrBefore(thisWeekEnd)
      })
    },
    lastWeekSchedules() {
      const lastWeekStart = moment(thisWeekStart).subtract(1, 'weeks')
      const lastWeekEnd = moment(thisWeekEnd).subtract(1, 'weeks')
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(lastWeekStart) && dt.isSameOrBefore(lastWeekEnd)
      })
    },
    nextWeekSchedules() {
      const nextWeekStart = moment(thisWeekStart).add(1, 'weeks')
      const nextWeekEnd = moment(thisWeekEnd).add(1, 'weeks')
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(nextWeekStart) && dt.isSameOrBefore(nextWeekEnd)
      })
    },
    studyProgresses() {
      return this.studyProgressList.data
    },
    groupedStudyProgressLessons() {
      return this.studyProgresses.reduce((carry, sp) => {
        if (!carry[sp.classroom_schedule_id]) {
          carry[sp.classroom_schedule_id] = []
        }
        if (sp.details.length > 0) {
          carry[sp.classroom_schedule_id] = sp.details.map((detail) => ({
            id: detail.lesson_id,
            name: detail.lesson_name,
            name_cn: detail.lesson_name_cn,
            lesson_category_id: detail.lesson_category_id,
          }))
        }
        return carry
      }, {})
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getScheduleList: 'classroomSchedule/getList',
      generateSchedules: 'classroom/generateSchedules',
      updateClassroom: 'classroom/update',
      getStudyProgressList: 'studyProgress/getList',
    }),
    async init() {
      _.merge(this.weeklySchedules, this.classroom.data.weekly_schedules)
      await Promise.all([
        this.getScheduleListData(),
        this.getStudyProgressList({
          classroom_id: this.classroomId,
        }),
      ])
    },
    async getScheduleListData() {
      await this.getScheduleList({
        classroom_id: this.classroomId,
      })
    },
    showEditScheduleDialog(item) {
      const schedule = this.schedules.find((e) => e.id === item.id)
      if (schedule) {
        this.editDialog.item = _.cloneDeep(schedule)
        this.editDialog.show = true
      }
    },
    resetEditScheduleDialog() {
      this.editDialog.index = -1
      this.editDialog.item = null
      this.editDialog.show = false
    },
    async onSubmitEditSchedule() {
      await this.getScheduleListData()
      this.resetEditScheduleDialog()
    },
    async saveChanges() {
      const req = JSON.parse(JSON.stringify(this.classroom.data))
      req.weekly_schedules = this.weeklySchedules
      await this.updateClassroom(req)
      window.location.reload()
    },
  },
}
</script>
